import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
    Mail, Plus, Trash2, Save, Star, Eye, EyeOff, Loader2, AlertCircle, Copy, Check,
    Sparkles, Send, Wand2, Trash
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../hooks/useAuth";
import {
    fetchEmailTemplates,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    assistantEmailTemplate
} from "../../services/crmServices/emailTemplateService";

// Sample lead data used to render the live preview.
const PREVIEW_VARS = {
    firstName: "John",
    lastName: "Doe",
    phone: "+44 20 1234 5678",
    email: "john.doe@example.com",
    country: "United Kingdom",
    city: "London",
    source: "Website",
    status: "New",
    subject: "",
    body: "",
    message: ""
};

const PLACEHOLDER_HINT = "{{firstName}} {{lastName}} {{phone}} {{email}} {{country}} {{city}} {{source}} {{status}}";

// Placeholders auto-filled from the lead. Anything else in a template is a
// "custom" variable the AI fills in per call (e.g. {{appointment_time}}).
const STANDARD_VARS = new Set([
    "firstName", "lastName", "phone", "email", "country", "city", "source",
    "status", "subject", "body", "message"
]);

function detectCustomVars(...texts) {
    const found = new Set();
    texts.filter(Boolean).forEach((t) => {
        (String(t).match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) || []).forEach((m) => {
            const key = m.replace(/[{}\s]/g, "");
            if (!STANDARD_VARS.has(key)) found.add(key);
        });
    });
    return [...found];
}

function renderPreview(template, htmlLineBreaks = false) {
    if (!template) return "";
    return String(template).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, key) => {
        let v = PREVIEW_VARS[key];
        if (v === undefined || v === null) v = "";
        if (htmlLineBreaks) v = String(v).replace(/\n/g, "<br/>");
        return String(v);
    });
}

const emptyDraft = () => ({
    _id: null,
    key: "",
    name: "",
    description: "",
    subject: "",
    html: "",
    enabled: true,
    isDefault: false
});

export default function EmailTemplates() {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null); // null = nothing, "new" = new draft
    const [draft, setDraft] = useState(emptyDraft());
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(true);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchEmailTemplates();
            const rows = res?.data || [];
            setTemplates(rows);
            return rows;
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load email templates");
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const selectTemplate = (tpl) => {
        setSelectedId(tpl._id);
        setDraft({
            _id: tpl._id,
            key: tpl.key || "",
            name: tpl.name || "",
            description: tpl.description || "",
            subject: tpl.subject || "",
            html: tpl.html || "",
            enabled: tpl.enabled !== false,
            isDefault: !!tpl.isDefault
        });
    };

    const startNew = () => {
        setSelectedId("new");
        setDraft(emptyDraft());
    };

    const applyProposal = (p) => {
        setSelectedId((cur) => cur || "new");
        setDraft((prev) => ({
            _id: prev._id || null,
            name: p.name || prev.name || "",
            key: p.key || prev.key || "",
            description: p.description || prev.description || "",
            subject: p.subject !== undefined ? p.subject : prev.subject,
            html: p.html !== undefined ? p.html : prev.html,
            enabled: prev.enabled !== undefined ? prev.enabled : true,
            isDefault: !!prev.isDefault
        }));
        toast.success("Applied to editor — review and Save");
    };

    const isNew = selectedId === "new";

    const handleSave = async () => {
        if (!draft.name.trim()) return toast.error("Name is required");
        if (!draft.subject.trim()) return toast.error("Subject is required");
        if (!draft.html.trim()) return toast.error("HTML body is required");

        setSaving(true);
        try {
            let res;
            if (isNew) {
                res = await createEmailTemplate({
                    name: draft.name,
                    key: draft.key || draft.name,
                    description: draft.description,
                    subject: draft.subject,
                    html: draft.html,
                    enabled: draft.enabled,
                    isDefault: draft.isDefault
                });
            } else {
                res = await updateEmailTemplate({
                    _id: draft._id,
                    name: draft.name,
                    key: draft.key,
                    description: draft.description,
                    subject: draft.subject,
                    html: draft.html,
                    enabled: draft.enabled,
                    isDefault: draft.isDefault
                });
            }
            if (res?.status === 200) {
                toast.success(isNew ? "Template created" : "Template saved");
                const rows = await load();
                const saved = res.data;
                if (saved?._id) {
                    const fresh = rows.find((r) => r._id === saved._id) || saved;
                    selectTemplate(fresh);
                }
            } else {
                toast.error(res?.message || "Save failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (isNew || !draft._id) {
            setSelectedId(null);
            return;
        }
        if (!window.confirm(`Delete the "${draft.name}" template? This cannot be undone.`)) return;
        setSaving(true);
        try {
            const res = await deleteEmailTemplate(draft._id);
            if (res?.status === 200) {
                toast.success("Template deleted");
                setSelectedId(null);
                setDraft(emptyDraft());
                load();
            } else {
                toast.error(res?.message || "Delete failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Delete failed");
        } finally {
            setSaving(false);
        }
    };

    const copyKey = (key) => {
        navigator.clipboard.writeText(key);
        setCopied(true);
        toast.success(`Copied "${key}"`);
        setTimeout(() => setCopied(false), 1500);
    };

    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6 py-12">
                <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                    <div className="p-3 bg-red-50 rounded-full w-fit mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-sm text-gray-500">Administrator privileges required.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-sm">
                        <Mail className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Email Templates</h1>
                        <p className="text-xs text-gray-500">
                            Named emails the AI caller can send with its <span className="font-mono">send_email</span> tool — it picks a template by its key
                        </p>
                    </div>
                </div>
                <Button size="sm" onClick={startNew}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> New template
                </Button>
            </div>

            {error && (
                <Card className="mb-4 border-red-200">
                    <CardContent className="p-4 flex items-center gap-2 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4" /> {error}
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_380px] gap-4">
                {/* Template list */}
                <div className="space-y-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-400">
                            <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                    ) : templates.length === 0 ? (
                        <Card>
                            <CardContent className="p-6 text-center text-sm text-gray-500">
                                No templates yet. Click <span className="font-medium">New template</span> to create your first one.
                            </CardContent>
                        </Card>
                    ) : (
                        templates.map((tpl) => (
                            <button
                                key={tpl._id}
                                onClick={() => selectTemplate(tpl)}
                                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                                    selectedId === tpl._id
                                        ? "border-indigo-400 bg-indigo-50/60 ring-1 ring-indigo-200"
                                        : "border-gray-200 bg-white hover:border-gray-300"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium text-sm text-gray-900 truncate">{tpl.name}</span>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {tpl.isDefault && (
                                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-0.5 px-1.5">
                                                <Star className="h-3 w-3" /> Default
                                            </Badge>
                                        )}
                                        {tpl.enabled === false && (
                                            <Badge variant="secondary" className="gap-0.5 px-1.5">
                                                <EyeOff className="h-3 w-3" /> Off
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-1 flex items-center gap-1.5">
                                    <code className="text-[11px] text-indigo-600 bg-indigo-50 rounded px-1.5 py-0.5">{tpl.key}</code>
                                </div>
                                {tpl.description && (
                                    <p className="mt-1.5 text-[11px] text-gray-500 line-clamp-2">{tpl.description}</p>
                                )}
                            </button>
                        ))
                    )}
                </div>

                {/* Editor */}
                {selectedId ? (
                    <Card>
                        <CardContent className="p-5 space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-sm font-semibold text-gray-900">
                                    {isNew ? "New template" : "Edit template"}
                                </h2>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={handleDelete} disabled={saving} className="text-red-600 hover:text-red-700">
                                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {isNew ? "Cancel" : "Delete"}
                                    </Button>
                                    <Button size="sm" onClick={handleSave} disabled={saving}>
                                        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                                        Save
                                    </Button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Name</label>
                                    <Input
                                        value={draft.name}
                                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                        placeholder="Appointment scheduled"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">
                                        Key (AI reference)
                                    </label>
                                    <div className="flex items-center gap-1.5">
                                        <Input
                                            value={draft.key}
                                            onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                                            placeholder="appointment_scheduled"
                                            className="font-mono text-xs"
                                        />
                                        {draft.key && (
                                            <Button variant="outline" size="sm" onClick={() => copyKey(draft.key)} className="shrink-0 h-9 px-2">
                                                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                            </Button>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-1">Leave blank to auto-generate from the name.</p>
                                </div>
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">
                                    When to use this template <span className="normal-case font-normal text-gray-400">(shown to the AI)</span>
                                </label>
                                <Input
                                    value={draft.description}
                                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                    placeholder="Send after the customer confirms an appointment time."
                                />
                            </div>

                            <div className="flex flex-wrap items-center gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <Switch checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} />
                                    <span className="text-sm text-gray-700">Enabled</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <Switch checked={draft.isDefault} onCheckedChange={(v) => setDraft({ ...draft, isDefault: v })} />
                                    <span className="text-sm text-gray-700 flex items-center gap-1">
                                        <Star className="h-3.5 w-3.5 text-amber-500" /> Default (used when the AI doesn't specify one)
                                    </span>
                                </label>
                            </div>

                            <div>
                                <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide block mb-1">Subject</label>
                                <Input
                                    value={draft.subject}
                                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                                    placeholder="Your appointment with {{firstName}}"
                                />
                                {draft.subject.trim() && (
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Preview: <span className="text-gray-800 font-medium">{renderPreview(draft.subject)}</span>
                                    </p>
                                )}
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">HTML body</label>
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setShowPreview((s) => !s)}>
                                        {showPreview ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                                        {showPreview ? "Hide preview" : "Show preview"}
                                    </Button>
                                </div>
                                <div className={`grid gap-3 ${showPreview && draft.html.trim() ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                                    <Textarea
                                        value={draft.html}
                                        onChange={(e) => setDraft({ ...draft, html: e.target.value })}
                                        placeholder="<html>...</html>"
                                        className="font-mono text-xs min-h-[340px]"
                                    />
                                    {showPreview && draft.html.trim() && (
                                        <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
                                            <iframe
                                                title="Email preview"
                                                srcDoc={renderPreview(draft.html, true)}
                                                className="w-full h-[340px]"
                                                sandbox=""
                                            />
                                        </div>
                                    )}
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                    Lead placeholders (auto-filled): <span className="font-mono">{PLACEHOLDER_HINT}</span>
                                </p>
                                {(() => {
                                    const customVars = detectCustomVars(draft.subject, draft.html);
                                    if (customVars.length === 0) return null;
                                    return (
                                        <div className="mt-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-md">
                                            <p className="text-[11px] text-indigo-800">
                                                <span className="font-semibold">The AI will fill these per call:</span>{" "}
                                                {customVars.map((v) => (
                                                    <code key={v} className="font-mono bg-white border border-indigo-200 rounded px-1.5 py-0.5 mr-1">{`{{${v}}}`}</code>
                                                ))}
                                            </p>
                                            <p className="text-[10px] text-indigo-600 mt-1">
                                                These aren't lead fields — tell the agent in its prompt to pass them when it sends this email (e.g. the confirmed appointment date/time).
                                            </p>
                                        </div>
                                    );
                                })()}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-10 text-center text-sm text-gray-500">
                            <Mail className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                            Select a template to edit, or create a new one.
                        </CardContent>
                    </Card>
                )}

                {/* AI assistant */}
                <AssistantPanel currentDraft={draft} onApply={applyProposal} />
            </div>

            {/* How the AI uses these */}
            <Card className="mt-4 border-indigo-100 bg-indigo-50/40">
                <CardContent className="p-4 text-xs text-gray-600 space-y-1.5">
                    <p className="font-semibold text-gray-700">How the AI caller chooses a template</p>
                    <p>
                        When the agent uses the <span className="font-mono">send_email</span> tool it passes a
                        {" "}<span className="font-mono">template</span> value — one of the keys above (e.g. <span className="font-mono">welcome</span>,
                        {" "}<span className="font-mono">appointment_scheduled</span>). If it doesn't specify one, the
                        {" "}<span className="font-mono">Default</span> template is sent. Tell the agent in its prompt which template to use in
                        which situation. The copyable tool schema on the AI Caller Integrations settings page already lists your current keys.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

const CHAT_KEY = "emailTemplateAssistantChat";
const WELCOME_MSG = {
    role: "assistant",
    content: "Hi! Tell me what email you need and I'll build it — e.g. \"an appointment scheduled email that includes the time\" or \"a company information email\". I can also tweak the template you have open."
};

function AssistantPanel({ currentDraft, onApply }) {
    const [messages, setMessages] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(CHAT_KEY) || "null");
            if (Array.isArray(saved) && saved.length > 0) return saved;
        } catch { /* ignore */ }
        return [WELCOME_MSG];
    });
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        try { localStorage.setItem(CHAT_KEY, JSON.stringify(messages.slice(-30))); } catch { /* ignore */ }
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        const nextMessages = [...messages, { role: "user", content: text }];
        setMessages(nextMessages);
        setInput("");
        setBusy(true);
        try {
            const history = nextMessages
                .filter((m) => m.role === "user" || m.role === "assistant")
                .map((m) => ({ role: m.role, content: m.content }));
            const res = await assistantEmailTemplate(history, {
                name: currentDraft?.name,
                key: currentDraft?.key,
                description: currentDraft?.description,
                subject: currentDraft?.subject,
                html: currentDraft?.html
            });
            if (res?.status === 200 && res.data) {
                setMessages((prev) => [...prev, {
                    role: "assistant",
                    content: res.data.reply || "Done.",
                    proposal: res.data.proposal || null
                }]);
            } else {
                setMessages((prev) => [...prev, { role: "assistant", content: res?.message || "Something went wrong." }]);
            }
        } catch (err) {
            setMessages((prev) => [...prev, { role: "assistant", content: err?.response?.data?.message || "Request failed." }]);
        } finally {
            setBusy(false);
        }
    };

    const clearChat = () => {
        setMessages([WELCOME_MSG]);
    };

    return (
        <Card className="flex flex-col h-[640px]">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-gradient-to-br from-violet-500 to-purple-600 rounded-md">
                        <Sparkles className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">AI Assistant</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-gray-500" onClick={clearChat}>
                    <Trash className="h-3 w-3 mr-1" /> Clear
                </Button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((m, i) => (
                    <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                        <div className={`max-w-[92%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${
                            m.role === "user" ? "bg-violet-600 text-white" : "bg-gray-100 text-gray-800"
                        }`}>
                            {m.content}
                            {m.proposal && (
                                <div className="mt-2 rounded-md bg-white border border-violet-200 p-2 text-gray-800">
                                    <p className="font-semibold text-[11px] text-violet-700 flex items-center gap-1">
                                        <Wand2 className="h-3 w-3" /> Proposed template
                                    </p>
                                    <p className="text-[11px] mt-1"><span className="text-gray-500">Name:</span> {m.proposal.name || "(unnamed)"}</p>
                                    <p className="text-[11px]"><span className="text-gray-500">Key:</span> <code className="font-mono">{m.proposal.key}</code></p>
                                    <p className="text-[11px]"><span className="text-gray-500">Subject:</span> {m.proposal.subject}</p>
                                    {m.proposal.variables?.length > 0 && (
                                        <p className="text-[11px] mt-1 text-indigo-700">
                                            AI fills: {m.proposal.variables.map((v) => <code key={v} className="font-mono mr-1">{`{{${v}}}`}</code>)}
                                        </p>
                                    )}
                                    <Button size="sm" className="mt-2 h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={() => onApply(m.proposal)}>
                                        Apply to editor
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg px-3 py-2 text-xs text-gray-500 flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
                        </div>
                    </div>
                )}
            </div>

            <div className="border-t border-gray-100 p-2.5">
                <div className="flex items-end gap-2">
                    <Textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder="Describe the email you need…"
                        className="min-h-[42px] max-h-28 text-xs resize-none"
                        disabled={busy}
                    />
                    <Button size="sm" onClick={send} disabled={busy || !input.trim()} className="shrink-0 h-[42px]">
                        <Send className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
        </Card>
    );
}
